import { useMemo, useState, useCallback } from 'react';

import { ChevronDownIcon } from '@/components/Icons';
import { Paragraph } from '@/components/Paragraph';
import { useDeviceBreakpoints } from '@/hooks/useDeviceBreakpoints';
import { StyledFlexColumn, StyledFlexRow } from '@/layout/FlexLayout';
import { ProductDayToDayDescription } from '@/pages/DayToDay/components/ProductDayToDayDescription';
import { ProductDayToDayOther } from '@/pages/DayToDay/components/ProductDayToDayOther';
import { StyledOtherButton, StyledItem, StyledContainer } from '@/pages/DayToDay/styles';
import { createDateDurationString } from '@/utils/createDateDurationString';
import { getIcon } from '@/utils/getIcon';

import type { ItineraryProduct } from '@/types/products';

const productTypeTranslation: Record<ItineraryProduct['type'], string> = {
  accommodation: 'Verblijf',
  excursion: 'Activiteit',
  flight: 'Vliegtuig',
  transfer: 'Transfer',
  carrental: 'Huurauto',
  ferry: 'Ferry',
};

interface ItineraryProductDayToDayProps {
  itineraryProduct: ItineraryProduct;
}

export function ItineraryProductDayToDay({ itineraryProduct }: ItineraryProductDayToDayProps) {
  const { isMobile } = useDeviceBreakpoints();
  const [showOther, setShowOther] = useState(false);
  const Icon = useMemo(() => () => getIcon(itineraryProduct.type, '2xl'), [itineraryProduct.type]);
  const productTypeLabel = useMemo(() => productTypeTranslation[itineraryProduct.type], [itineraryProduct.type]);
  const productStartDate = useMemo(
    () => createDateDurationString(itineraryProduct.startDate, itineraryProduct.endDate),
    [itineraryProduct.endDate, itineraryProduct.startDate],
  );

  const toggleShow = useCallback(() => setShowOther((prev) => !prev), []);

  if (isMobile)
    return (
      <StyledContainer>
        <StyledFlexRow>
          <StyledFlexColumn alignItems="center" justifyContent="center">
            <Icon />
          </StyledFlexColumn>
          <StyledFlexColumn paddingLeft={3}>
            <Paragraph color="primary">
              {productTypeLabel} - {productStartDate}
            </Paragraph>
            <Paragraph size="xl" weight={600} paddingBottom={1} paddingTop={1}>
              {itineraryProduct.name}
            </Paragraph>
          </StyledFlexColumn>
        </StyledFlexRow>
        <StyledItem flex="1">
          <ProductDayToDayDescription itineraryProduct={itineraryProduct} />
        </StyledItem>
        <StyledItem flex="1">
          <StyledOtherButton type="button" onClick={toggleShow} $open={showOther}>
            Overige informatie <ChevronDownIcon />
          </StyledOtherButton>
          {showOther && <ProductDayToDayOther itineraryProduct={itineraryProduct} />}
        </StyledItem>
      </StyledContainer>
    );

  return (
    <StyledFlexRow>
      <Icon />
      <StyledItem paddingLeft={4} flex="1">
        <Paragraph color="primary">
          {productTypeLabel} - {productStartDate}
        </Paragraph>
        <Paragraph size="xl" weight={600} paddingBottom={1} paddingTop={1}>
          {itineraryProduct.name}
        </Paragraph>
        <ProductDayToDayDescription itineraryProduct={itineraryProduct} />
      </StyledItem>
      <StyledItem marginLeft="auto" flex="1">
        <Paragraph color="primary" onClick={toggleShow}>
          Overige informatie
        </Paragraph>
        <ProductDayToDayOther itineraryProduct={itineraryProduct} />
      </StyledItem>
    </StyledFlexRow>
  );
}
